<script setup lang="ts">

/*
	DecorativeShapeItem.vue

	Renderable decorative rect/rounded-rect shape (e.g. a stage or screen block).
	Rendering intentionally mirrors the production seat map viewer's `makeRRect`
	(see SeatMap.vue) so what's drawn here matches what customers will see.

	Features:
	- Draggable positioning with position-changed emission on drag end
	- Resizable via a bottom-right corner handle, with live preview while dragging
	- Selection outline when selected
	- Optional border stroke and optional centered label text (falls back to
	  "SCREEN" when no text is set and the subtype is missing/'screen', same as production)
*/

import { computed } from 'vue'
import { MIN_SHAPE_SIZE } from '~/lib/seatmapeditor/constants'
import type { DecorativeShape } from '~/lib/seatmapeditor/types'

interface Props {
	shape: DecorativeShape
	isSelected: boolean
}

interface Emits {
	(e: 'select'): void
	(e: 'position-changed', x: number, y: number): void
	(e: 'resize-changed', w: number, h: number): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { $translate } = useNuxtApp()
const _ = $translate as (key: string) => string

const HANDLE_SIZE = 16
const HANDLE_HALF = HANDLE_SIZE / 2

const cornerRadius = computed(() => (props.shape.type === 'rect' ? 0 : (props.shape.r ?? 0)))

const displayText = computed(() => {
	if (props.shape.text) {
		return props.shape.text
	}
	if (!props.shape.subtype || props.shape.subtype === 'screen') {
		return _('Screen').toUpperCase()
	}
	return ''
})

const onDragEnd = (event: any) => {
	emit('position-changed', event.target.x(), event.target.y())
}

const onResizeDragStart = (event: any) => {
	event.cancelBubble = true
}

const onResizeDrag = (event: any) => {
	event.cancelBubble = true
	const node = event.target
	const w = Math.max(MIN_SHAPE_SIZE, node.x() + HANDLE_HALF)
	const h = Math.max(MIN_SHAPE_SIZE, node.y() + HANDLE_HALF)
	// Keep the handle glued to the (possibly clamped) bottom-right corner so it doesn't
	// visually detach from the shape when dragged past the minimum size.
	node.x(w - HANDLE_HALF)
	node.y(h - HANDLE_HALF)
	emit('resize-changed', w, h)
}
</script>

<template>
	<v-group
		:config="{
			x: shape.x,
			y: shape.y,
			draggable: true,
			onDragEnd,
			onClick: () => emit('select'),
			onTap: () => emit('select')
		}"
	>
		<v-rect
			v-if="isSelected"
			:config="{
				x: -4,
				y: -4,
				width: shape.w + 8,
				height: shape.h + 8,
				stroke: '#0284c7',
				strokeWidth: 2,
				dash: [6, 4],
				cornerRadius: cornerRadius + 4,
				listening: false,
				perfectDrawEnabled: false
			}"
		/>

		<v-rect
			:config="{
				x: 0,
				y: 0,
				width: shape.w,
				height: shape.h,
				cornerRadius,
				fill: shape.fill,
				stroke: shape.border || undefined,
				strokeWidth: shape.border ? 2 : 0,
				perfectDrawEnabled: false
			}"
		/>

		<v-text
			v-if="displayText"
			:config="{
				x: 0,
				y: 0,
				width: shape.w,
				height: shape.h,
				text: displayText,
				verticalAlign: 'middle',
				letterSpacing: 1,
				fontSize: 10,
				fontStyle: 'bold',
				fontFamily: 'Oxygen, sans-serif',
				align: 'center',
				listening: false,
				perfectDrawEnabled: false
			}"
		/>

		<v-rect
			v-if="isSelected"
			:config="{
				x: shape.w - HANDLE_HALF,
				y: shape.h - HANDLE_HALF,
				width: HANDLE_SIZE,
				height: HANDLE_SIZE,
				fill: '#ffffff',
				stroke: '#0284c7',
				strokeWidth: 2,
				cornerRadius: 3,
				draggable: true,
				onDragStart: onResizeDragStart,
				onDragMove: onResizeDrag,
				onDragEnd: onResizeDrag
			}"
		/>
	</v-group>
</template>
